// noinspection ES6PreferShortImport
import { ProductError, ProductErrorType } from '../models/error';
// noinspection ES6PreferShortImport
import { ProductGraphicPreferenceStatus, ProductSoundPreferenceStatus } from '../models/product';
// noinspection ES6PreferShortImport
import { ScoreFindStrategy } from '../models/progression';
import { Game } from './game';
import { TrophyConfig } from './trophy';
// noinspection ES6PreferShortImport
import { CustomImage } from '../models/style';

/**
 * ## Configuration commune à tous les jeux
 */
export class GameConfig {
  /**
   * Langue principale du jeu
   */
  local?: string;
  /**
   * Permet ou non d'afficher la correction du jeu
   */
  showAnswer?: boolean;
  /**
   * par défaut à vrai
   */
  showScore?: boolean;
  /**
   * seuil à partir duquel il faut considérer que le jeu est gagné (valeur comprise etre 0 et 1)
   */
  victoryThreshold?: number;
  /**
   * Donne le type de jeu à ouvrir
   * permet de n'avoir qu'un seul lecteur de jeu générique
   */
  gameType?: Game;
  /**
   * Permet de choisir le mode d'affichage du jeu
   */
  displayMode?: GameDisplayMode;
  /**
   * Permet de ne pas ajouter de trophé au calcul du score
   */
  noTrophees?: boolean;
  /**
   * Regroupe les infos nécessaire au calcul de l'attribution des trophés avec le poid dans les scores
   */
  trophyConfig?: TrophyConfig;
  /**
   * Permet de choisir la difficulté depuis le fichier de config
   * est ignoré si le selecteur de niveau est visible
   * exemple: permt de choisir la vitesse et le mode de déplacement dans le from-the-sky
   */
  difficulty?: GameLevel;
  /**
   * Gestion des images et thème
   */
  graphicsConfig?: GraphicsConfig;
  /**
   * Gestion des sons du jeu
   */
  soundConfig?: SoundConfig;
  /**
   * Gestion de comment le score sera calculer
   */
  strategy?: ScoreFindStrategy;
  /**
   * si vrai: ajoute le secteur de niveau au début du jeu
   */
  showDifficultiesLevels?: boolean;
  /*
   * Afficher le bouton "Rejouer" lorsqu'un jeu est terminé
   * */
  canReplayGame?: boolean;

  /*
   * Permet de rejouer indéfiniment le jeu
   * */
  hasReplayLimit?: boolean;

  /*
   * Nombre maximal de tentative
   * */
  totalNumberReplay?: number;

  /*
   * Affiche le compteur durant un jeu
   * */
  hasTimer?: boolean;

  /*
   * Durée en seconde pour la réalisation du jeu
   * */
  timeToPlay?: number;

  /**
   * Hide privacy (rgpd module)
   */
  hidePrivacy?: boolean;

  /**
   * Permet de changer d'echelle de notation
   */

  /**
   * Permet d'afficher le score avec le bon format
   */
  specificGradingScale?: SpecificGradingScale;

  constructor(config?: GameConfig) {
    this.showAnswer = config?.showAnswer ?? true;
    this.local = config?.local;
    this.showScore = config?.showScore ?? true;
    this.victoryThreshold = config?.victoryThreshold;
    this.gameType = config?.gameType;
    this.displayMode = config?.displayMode || GameDisplayMode.STANDARD;
    this.noTrophees = config?.noTrophees ?? true;
    this.trophyConfig = config ? new TrophyConfig(config.trophyConfig) : null;
    this.difficulty = config?.difficulty;
    this.graphicsConfig = new GraphicsConfig(config?.graphicsConfig);
    this.soundConfig = new SoundConfig(config?.soundConfig);
    this.strategy = config?.strategy ?? ScoreFindStrategy.BEST_SCORE;
    this.showDifficultiesLevels = config?.showDifficultiesLevels;
    this.canReplayGame = config?.canReplayGame ?? true;
    this.hidePrivacy = config?.hidePrivacy ?? false;
    this.hasReplayLimit = config?.hasReplayLimit ?? false;
    this.totalNumberReplay = config?.totalNumberReplay ?? null;
    this.hasTimer = config?.hasTimer ?? false;
    this.timeToPlay = config?.timeToPlay ?? null;
    this.specificGradingScale = config?.specificGradingScale ?? null;
  }

  static validate(config: GameConfig, parentPath: string, payloadData: any): ProductError[] {
    const errors: ProductError[] = [];

    if (config.victoryThreshold < 0 || config.victoryThreshold > 1) {
      errors.push({
        payload: { ...payloadData },
        property: parentPath + '.victoryThreshold',
        type: ProductErrorType.OUT_OF_RANGE,
      });
    }
    if (config.trophyConfig?.thresholds.gold < 0 || config.trophyConfig?.thresholds.gold > 1) {
      errors.push({
        payload: { ...payloadData },
        property: parentPath + '.thresholds.gold',
        type: ProductErrorType.OUT_OF_RANGE,
      });
    }
    if (config.trophyConfig?.thresholds.silver < 0 || config.trophyConfig?.thresholds.silver > 1) {
      errors.push({
        payload: { ...payloadData },
        property: parentPath + '.thresholds.silver',
        type: ProductErrorType.OUT_OF_RANGE,
      });
    }
    if (config.trophyConfig?.thresholds.bronze < 0 || config.trophyConfig?.thresholds.bronze > 1) {
      errors.push({
        payload: { ...payloadData },
        property: parentPath + '.thresholds.bronze',
        type: ProductErrorType.OUT_OF_RANGE,
      });
    }

    return errors;
  }
}

export enum GameDisplayMode {
  STANDARD = 'standard',
  STANDALONE = 'standalone',
  EMBEDDED = 'embedded',
}

/**
 * Regroupe tous les paramètres liés aux sons
 */
export class SoundConfig {
  /**
   * Si non precisé explicitement à "false", sera considéré comme "true"
   */
  initiallyOn?: boolean;

  status?: ProductSoundPreferenceStatus;
  deactivateAmbianceSound?: boolean;
  deactivateGameSound?: boolean;
  /**
   * Musique pour le jeu
   * si non renseigné, pas de musique
   */
  gameMusic?: string;

  constructor(config?: SoundConfig) {
    this.status = config?.status ? config.status : ProductSoundPreferenceStatus.DEFAULT;
    this.initiallyOn = config?.initiallyOn ?? false;
    this.deactivateGameSound = config?.deactivateGameSound ?? false;
    this.deactivateAmbianceSound = config?.deactivateAmbianceSound ?? false;
    this.gameMusic = config?.gameMusic;
  }
}

/**
 * Regroupe tous les paramètres liés au thème CSS et images
 */
export class GraphicsConfig {
  /**
   * référence les thèmes à ajouter pour surcharger l'IHM
   */
  themesCSS?: string[];
  /**
   * Permet de surcharger l'image du background
   */
  backgroundUrl?: string;
  /**
   * Permet de surcharger l'image du portrait dans l'intro
   */
  portraitUrl?: string;
  /**
   * Permet de surcharger le favicon
   */
  faviconUrl?: string;
  status: ProductGraphicPreferenceStatus;

  standaloneTextFont?: string;
  standaloneTitleFont?: string;
  standaloneTitleRatio?: string;
  standaloneTitleLetterSpacing?: string;
  standalonePrimary?: string;
  standaloneDarkCustom?: string;

  background?: CustomImage;
  poster?: CustomImage;

  constructor(config: GraphicsConfig) {
    this.themesCSS = config?.themesCSS && config?.themesCSS.length ? [...config.themesCSS] : [];
    this.backgroundUrl = config?.backgroundUrl || 'assets/shared/images/game-background/pattern/pink-blue.jpg';
    this.portraitUrl = config?.portraitUrl || 'assets/shared/images/game-background/avatar/shape-3.jpg';
    this.faviconUrl = config?.faviconUrl || 'assets/shared/images/branding/favicon-z.svg';
    this.standaloneTextFont = config?.standaloneTextFont || "'Inter', sans-serif";
    this.standaloneTitleFont = config?.standaloneTitleFont || "'DM Sans', sans-serif";
    this.standaloneTitleRatio = config?.standaloneTitleRatio || '0px';
    this.standaloneTitleLetterSpacing = config?.standaloneTitleLetterSpacing || '1';
    this.standalonePrimary = config?.standalonePrimary || '#e4595b';
    this.standaloneDarkCustom = config?.standaloneDarkCustom || '#173640';
    this.status = config?.status ? config.status : ProductGraphicPreferenceStatus.DEFAULT;
    this.poster = new CustomImage(config?.poster);
    this.background = new CustomImage(config?.background);
  }
}

export enum GameLevel {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard',
}

export enum SpecificGradingScale {
  MARK_5 = 'mark5',
  MARK_10 = 'mark10',
  MARK_20 = 'mark20',
  MARK_50 = 'mark50',
  MARK_100 = 'mark100',
  MARK_LETTERS = 'markLetters',
  MARK_SMILEYS = 'markSmileys',
}
